import { useId, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { Item, Location, Photo } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { Alert, ErrorAlert, Field } from '../components/ui'

/** Saisie rapide : photo (facultative) + nom facultatif + rangement. */
export function AddItemPage() {
  const { t } = useTranslation()
  const { current } = useHousehold()
  const [params] = useSearchParams()
  const presetLocation = params.get('locationId')
  const fileId = useId()
  const [name, setName] = useState('')
  const [locationId, setLocationId] = useState(presetLocation ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [created, setCreated] = useState<Item | null>(null)
  const [busy, setBusy] = useState(false)

  const locations = useQuery({
    queryKey: ['locations', current?.id],
    enabled: Boolean(current?.id),
    queryFn: () => api.get<{ items: Location[] }>(`/households/${current!.id}/locations`),
  })

  if (!current) return <p role="status">{t('common.loading')}</p>
  const canWrite = current.role !== 'viewer' && !current.readOnly

  if (!canWrite) {
    return (
      <>
        <h1>{t('inventory.addItemTitle')}</h1>
        <Alert kind="warning">{t('inventory.readOnlyHint')}</Alert>
        <Link className="btn secondary" to="/">
          {t('common.back')}
        </Link>
      </>
    )
  }

  if (created) {
    const label = created.name?.trim() || t('inventory.untitledItem')
    return (
      <>
        <h1>{t('inventory.itemCreated')}</h1>
        <Alert kind="success">{label}</Alert>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setCreated(null)
              setName('')
              setFile(null)
              setPreview(null)
            }}
          >
            {t('inventory.addAnother')}
          </button>
          <Link className="btn secondary" to={created.locationId ? `/storage?locationId=${created.locationId}` : '/storage'}>
            {t('inventory.seeStorage')}
          </Link>
          <Link className="btn ghost" to={`/items/${created.id}`}>
            {label}
          </Link>
        </div>
      </>
    )
  }

  return (
    <>
      <h1>{t('inventory.addItemTitle')}</h1>
      <ErrorAlert error={error} />
      <form
        className="stack"
        onSubmit={async (e: FormEvent) => {
          e.preventDefault()
          setError(null)
          setBusy(true)
          try {
            const item = await api.post<Item>(`/households/${current.id}/items`, {
              name: name.trim() || null,
              locationId: locationId || null,
            })
            if (file) {
              const form = new FormData()
              form.append('file', file, file.name || 'photo.jpg')
              await api.postForm<Photo>(`/households/${current.id}/items/${item.id}/photos`, form)
            }
            setCreated(item)
          } catch (err) {
            setError(err)
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label htmlFor={fileId}>{t('inventory.photo')}</label>
          <span className="hint">{t('inventory.photoHint')}</span>
          <input
            id={fileId}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(e) => {
              const next = e.target.files?.[0] ?? null
              setFile(next)
              setPreview((prev) => {
                if (prev) URL.revokeObjectURL(prev)
                return next ? URL.createObjectURL(next) : null
              })
            }}
          />
          {preview && (
            <img src={preview} alt={t('inventory.photoPreviewAlt')} className="photo-preview" />
          )}
        </div>
        <Field
          label={t('inventory.itemNameOptional')}
          hint={t('inventory.itemNameHint')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
        />
        <div className="field">
          <label htmlFor="item-location">{t('inventory.itemLocation')}</label>
          <select id="item-location" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">{t('inventory.itemLocationNone')}</option>
            {locations.data?.items.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {'\u00A0'.repeat(loc.depth * 2)}
                {loc.name}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn" disabled={busy}>
          {t('inventory.createItem')}
        </button>
      </form>
    </>
  )
}
